/**
 * US ZIP lookup. Derives city and state so the form does not ask for them.
 * Loaded on demand: the dataset is large and only a US address needs it.
 */

export interface ZipLocation {
  city: string;
  stateCode: string;
}

export type ZipLookup = (zipCode: string) => ZipLocation | null;

let pending: Promise<ZipLookup> | null = null;

/** Resolves once per session. The bundler caches the chunk after that. */
export const loadZipLookup = (): Promise<ZipLookup> => {
  pending ??= import("zipcodes-us")
    .then(
      ({ find }) =>
        (zipCode: string) => {
          const info = find(zipCode);
          return info.isValid
            ? { city: info.city, stateCode: info.stateCode }
            : null;
        },
    )
    .catch((error: unknown) => {
      pending = null;
      throw error;
    });
  return pending;
};

/**
 * The dataset is keyed by the 5-digit prefix, so ZIP+4 resolves to the same
 * place. Anything else is still being typed.
 */
export const zipLookupKey = (zipCode: string): string | null => {
  const digits = zipCode.replace(/\D/g, "");
  return digits.length === 5 || digits.length === 9
    ? digits.slice(0, 5)
    : null;
};
